import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { qrcodeAPIService } from '~/api/qrcode';
import { DEFAULT_QR_CODE_STYLES } from '~/constants/common';

// --- Types ---
interface QrCodeState {
  data: Record<string, any>;
  qrCodeStyles: any | null;
  qrStyle: any;
  selectedQR: any | null;
  isUriValid: boolean | null;
  loading: boolean;
  error: any;
  isAnonCreateQr: boolean;
  loadingSet: {
    isCreateQrcodeLoading: boolean;
    isUpdateQrcodeLoading: boolean;
    isQrcodeStylesLoading: boolean;
  };
}

// --- Initial State ---
const initialState: QrCodeState = {
  data: {},
  qrCodeStyles: null,
  qrStyle: DEFAULT_QR_CODE_STYLES,
  selectedQR: null,
  isUriValid: null,
  loading: false,
  error: null,
  isAnonCreateQr: false,
  loadingSet: {
    isCreateQrcodeLoading: false,
    isUpdateQrcodeLoading: false,
    isQrcodeStylesLoading: false,
  },
};

// --- Thunks ---
export const createQrCodeThunk = createAsyncThunk<any, { payload: any; action?: () => void }>(
  'qrCodeSlice/createQrCodeThunk',
  async (params, { rejectWithValue }) => {
    try {
      const response = await qrcodeAPIService.createQrCode(params.payload);

      if (params.action) params.action();

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateQrCodeThunk = createAsyncThunk<any, any>(
  'qrCodeSlice/updateQrCodeThunk',
  async (body, { getState, rejectWithValue }) => {
    try {
      const { qrCodeReducer } = getState() as { qrCodeReducer: QrCodeState };

      const response = await qrcodeAPIService.updateQrCode(body, qrCodeReducer?.data?.id || body.id);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const createQrCodeStylesThunk = createAsyncThunk<any, boolean>(
  'qrCodeSlice/createQrCodeStylesThunk',
  async (shouldLoad, { rejectWithValue }) => {
    try {
      if (!shouldLoad) return;

      const response = await qrcodeAPIService.getQrCodeStyles();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// --- Slice ---
export const qrCodeSlice = createSlice({
  name: 'qrCodeSlice',
  initialState,
  reducers: {
    setState(state, action: PayloadAction<QrCodeState>) {
      return action.payload;
    },
    setSelectedQr(state, action: PayloadAction<any>) {
      state.selectedQR = action.payload;
    },
    setData(state, action: PayloadAction<Record<string, any>>) {
      state.data = action.payload;
    },
    setIsAnonCreateQr(state, action: PayloadAction<boolean>) {
      state.isAnonCreateQr = action.payload;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // qrCode create
      .addCase(createQrCodeThunk.pending, (state) => {
        state.loading = true;
        state.loadingSet.isCreateQrcodeLoading = true;
        state.error = null;
      })
      .addCase(createQrCodeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingSet.isCreateQrcodeLoading = false;
        state.data = action.payload;
      })
      .addCase(createQrCodeThunk.rejected, (state, action) => {
        state.loading = false;
        state.loadingSet.isCreateQrcodeLoading = false;
        state.error = action.payload;
      })
      // qrCode update
      .addCase(updateQrCodeThunk.pending, (state, action) => {
        state.loading = true;
        state.loadingSet.isUpdateQrcodeLoading = true;
        state.error = null;
        state.data.style = action.meta.arg.style;
      })
      .addCase(updateQrCodeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingSet.isUpdateQrcodeLoading = false;
        state.data = action.payload;
      })
      .addCase(updateQrCodeThunk.rejected, (state, action) => {
        state.loading = false;
        state.loadingSet.isUpdateQrcodeLoading = false;
        state.error = action.payload;
      })
      // qrCode styles
      .addCase(createQrCodeStylesThunk.pending, (state) => {
        state.loading = true;
        state.loadingSet.isQrcodeStylesLoading = true;
        state.error = null;
      })
      .addCase(createQrCodeStylesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingSet.isQrcodeStylesLoading = false;
        state.qrCodeStyles = action.payload;
      })
      .addCase(createQrCodeStylesThunk.rejected, (state, action) => {
        state.loading = false;
        state.loadingSet.isQrcodeStylesLoading = false;
        state.error = action.payload;
      });
  },
});

export const { setIsAnonCreateQr } = qrCodeSlice.actions;
export default qrCodeSlice.reducer;
